import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { FlagAssetModal } from './FlagAssetModal';
import { PhotoGalleryModal } from './PhotoGalleryModal';
import {
  MapPin,
  AlertCircle,
  CheckCircle2,
  Clock,
  Flag,
  Wrench,
  MessageSquarePlus,
  Eye,
  Camera,
  MoreVertical,
  ChevronRight,
  Pencil,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { AssetAuditProgress } from '@/types/audit';

interface AssetProgressCardProps {
  asset: AssetAuditProgress;
  onEdit?: (assetId: string) => void;
  onFlagForReview: (assetId: string, updatedNotes?: string) => void;
  onAssignWorkItem: (assetId: string) => void;
  onAddComment: (assetId: string) => void;
  onViewDetails: (assetId: string) => void;
}

export function AssetProgressCard({
  asset,
  onEdit,
  onFlagForReview,
  onAssignWorkItem,
  onAddComment,
  onViewDetails,
}: Readonly<AssetProgressCardProps>) {
  const [showFlagModal, setShowFlagModal] = useState(false);
  const [showPhotoGallery, setShowPhotoGallery] = useState(false);

  const handleFlagConfirm = (reason: string) => {
    const currentUser = 'John Smith';
    const timestamp = new Date().toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });

    const flagNote = `${reason} - ${currentUser}, ${timestamp}`;
    const updatedNotes = asset.notes ? `${asset.notes}\n\n${flagNote}` : flagNote;

    if (onFlagForReview) {
      onFlagForReview(asset.id, updatedNotes);
    }
  };

  const handleFlagClick = () => {
    if (asset.status === 'flagged') {
      if (onFlagForReview) {
        onFlagForReview(asset.id);
      }
    } else {
      setShowFlagModal(true);
    }
  };

  const getStatusConfig = () => {
    switch (asset.status) {
      case 'verified':
        return {
          icon: CheckCircle2,
          label: 'Verified',
          className: 'bg-emerald-50 text-emerald-700 border-emerald-200',
        };
      case 'in-progress':
        return {
          icon: Clock,
          label: 'In Progress',
          className: 'bg-blue-50 text-blue-700 border-blue-200',
        };
      case 'not-started':
        return {
          icon: Clock,
          label: 'Not Started',
          className: 'bg-slate-50 text-slate-600 border-slate-200',
        };
      case 'flagged':
        return {
          icon: Flag,
          label: 'Flagged',
          className: 'bg-orange-50 text-orange-700 border-orange-200',
        };
      case 'missing':
        return {
          icon: Flag,
          label: 'Missing',
          className: 'bg-red-50 text-red-700 border-red-200',
        };
      default:
        return {
          icon: Clock,
          label: 'Not Started',
          className: 'bg-slate-50 text-slate-600 border-slate-200',
        };
    }
  };

  const getConditionConfig = () => {
    switch (asset.condition) {
      case 'good':
        return {
          icon: CheckCircle2,
          label: 'Good',
          className: 'text-emerald-700',
        };
      case 'attention':
        return {
          icon: AlertCircle,
          label: 'Needs Attention',
          className: 'text-amber-700',
        };
      case 'critical':
        return {
          icon: AlertCircle,
          label: 'Critical',
          className: 'text-red-700',
        };
      default:
        return null;
    }
  };

  const getLocationStatusIcon = () => {
    switch (asset.locationStatus) {
      case 'confirmed':
        return <CheckCircle2 className="h-4 w-4 text-emerald-600" />;
      case 'changed':
        return <AlertCircle className="h-4 w-4 text-amber-600" />;
      default:
        return <Clock className="h-4 w-4 text-slate-400" />;
    }
  };

  const statusConfig = getStatusConfig();
  const conditionConfig = getConditionConfig();
  const StatusIcon = statusConfig.icon;

  return (
    <>
      <div
        className={cn(
          'group relative bg-white border rounded-lg overflow-hidden transition-all duration-200 hover:shadow-md'
        )}
      >
        {/* Status Bar - Left Edge */}
        <div
          className={cn(
            'absolute left-0 top-0 bottom-0 w-12 flex items-center justify-center',
            asset.status === 'verified' && 'bg-emerald-500',
            asset.status === 'flagged' && 'bg-orange-500',
            asset.status === 'missing' && 'bg-red-500',
            asset.status === 'in-progress' && 'bg-blue-500',
            asset.status === 'not-started' && 'bg-slate-300'
          )}
        >
          <StatusIcon className="h-5 w-5 text-white" />
        </div>

        {/* Header - Asset Information (Colored Background) */}
        <div className="bg-slate-50 border-b px-4 py-3 pl-4 ml-12">
          <div className="flex items-center justify-between gap-4">
            {/* Asset Details - Single Line */}
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-base text-slate-900 truncate flex items-center gap-2">
                <span>{asset.assetName}</span>
                <span className="text-slate-400 font-normal">•</span>
                <span className="font-mono text-sm text-slate-600 font-normal">{asset.assetCode}</span>
                <span className="text-slate-400 font-normal">•</span>
                <span className="text-sm text-slate-600 font-normal">{asset.category}</span>
                {asset.flaggedForReview && (
                  <>
                    <span className="text-slate-400 font-normal">•</span>
                    <Flag className="h-4 w-4 text-orange-600 flex-shrink-0" />
                  </>
                )}
              </h3>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-1 flex-shrink-0">
              <Button variant="outline" size="sm" onClick={() => onViewDetails(asset.id)}>
                <Eye className="h-4 w-4 mr-2" />
                View Details
              </Button>
              
              {onEdit && (
                <Button variant="outline" size="sm" onClick={() => onEdit(asset.id)}>
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              )}
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={handleFlagClick}>
                    <Flag className="h-4 w-4 mr-2" />
                    {asset.status === 'flagged' ? 'Unflag Asset' : 'Flag for Review'}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onAddComment(asset.id)}>
                    <MessageSquarePlus className="h-4 w-4 mr-2" />
                    Add Comment
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => onAssignWorkItem(asset.id)}>
                    <Wrench className="h-4 w-4 mr-2" />
                    Raise Work Item
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>

        {/* Content - Fields (White Background) */}
        <div className="p-4 ml-12">
          <div className="grid grid-cols-12 gap-4">
            {/* Location */}
            <div className="col-span-3">
              <div className="flex items-center gap-1.5 mb-2">
                <MapPin className="h-4 w-4 text-slate-500" />
                <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Location</span>
              </div>
              <div className="space-y-1">
                {asset.locationStatus === 'pending' ? (
                  <div className="text-sm text-slate-500 italic">Awaiting assessment</div>
                ) : (
                  <>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-900">{asset.currentLocation}</span>
                      {getLocationStatusIcon()}
                    </div>
                    {asset.locationStatus === 'changed' && asset.previousLocation && (
                      <div className="flex items-center gap-1 text-xs text-amber-700">
                        <ChevronRight className="h-3 w-3" />
                        <span>from {asset.previousLocation}</span>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Condition */}
            <div className="col-span-3">
              <div className="flex items-center gap-1.5 mb-2">
                <AlertCircle className="h-4 w-4 text-slate-500" />
                <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Condition</span>
              </div>
              {conditionConfig ? (
                <div className="flex items-center gap-2">
                  <conditionConfig.icon className={cn('h-4 w-4', conditionConfig.className)} />
                  <span className={cn('text-sm font-medium', conditionConfig.className)}>
                    {conditionConfig.label}
                  </span>
                </div>
              ) : (
                <div className="text-sm text-slate-500 italic">Not assessed</div>
              )}
            </div>

            {/* Photos */}
            <div className="col-span-2">
              <div className="flex items-center gap-1.5 mb-2">
                <Camera className="h-4 w-4 text-slate-500" />
                <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Photos</span>
              </div>
              {asset.photos.length > 0 ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-9 px-3 -ml-3 hover:bg-slate-100"
                  onClick={() => setShowPhotoGallery(true)}
                >
                  <Camera className="h-4 w-4 mr-1.5 text-slate-600" />
                  <span className="text-sm font-medium text-slate-900">{asset.photos.length}</span>
                  <span className="text-sm text-slate-600 ml-1">
                    {asset.photos.length === 1 ? 'photo' : 'photos'}
                  </span>
                </Button>
              ) : (
                <div className="text-sm text-slate-500 italic">No photos</div>
              )}
            </div>

            {/* Notes */}
            <div className="col-span-4">
              <div className="flex items-center gap-1.5 mb-2">
                <MessageSquarePlus className="h-4 w-4 text-slate-500" />
                <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Notes</span>
              </div>
              {asset.notes && asset.notes.trim() !== '' ? (
                <div className="text-sm text-slate-700 line-clamp-2 leading-relaxed">
                  {asset.notes}
                </div>
              ) : (
                <div className="text-sm text-slate-500 italic">No notes</div>
              )}
            </div>
          </div>
        </div>
      </div>

      <FlagAssetModal
        open={showFlagModal}
        onOpenChange={setShowFlagModal}
        onConfirm={handleFlagConfirm}
        assetName={asset.name ?? asset.assetName}
      />

      <PhotoGalleryModal
        open={showPhotoGallery}
        onOpenChange={setShowPhotoGallery}
        photos={asset.photos}
        assetName={asset.assetName}
      />
    </>
  );
}
